// ./src/components/features/Submissions/SubmissionDetailsDrawer/SubmissionDetailsDrawer.tsx
import React from "react";
import { Stack, Divider, Menu, Skeleton, Group, Box } from "@mantine/core";
import { AppDrawer, AppButton, AppIcon } from "@/components/generic";
import { Form, FormField, Submission } from "@/utils/api";
import { usePdfDownload } from "@/hooks";
import { useApp } from "@/contexts";
import { SubmissionMetadata } from "./SubmissionMetadata";
import { SubmissionFormData } from "./SubmissionFormData";

import { OPERATION_RIGHTS } from "@/config";

/** Skeleton loading state for submission details */
const SubmissionDetailsSkeleton: React.FC = () => (
  <Stack gap="xl">
    {/* Metadata skeleton */}
    <Stack gap="md">
      <Group justify="space-between">
        <Skeleton height={24} width={120} radius="sm" />
        <Skeleton height={28} width={100} radius="xl" />
      </Group>
      <Box>
        <Group gap="xl">
          <Stack gap={4}>
            <Skeleton height={12} width={80} radius="sm" />
            <Skeleton height={16} width={140} radius="sm" />
          </Stack>
          <Stack gap={4}>
            <Skeleton height={12} width={60} radius="sm" />
            <Skeleton height={16} width={100} radius="sm" />
          </Stack>
          <Stack gap={4}>
            <Skeleton height={12} width={70} radius="sm" />
            <Skeleton height={16} width={120} radius="sm" />
          </Stack>
        </Group>
      </Box>
    </Stack>

    <Divider />

    {/* Form data skeleton */}
    <Stack gap="md">
      <Skeleton height={20} width={100} radius="sm" />
      {[...Array(5)].map((_, i) => (
        <Stack key={i} gap={4}>
          <Skeleton height={14} width={100} radius="sm" />
          <Skeleton height={36} width="100%" radius="sm" />
        </Stack>
      ))}
    </Stack>
  </Stack>
);

export interface SubmissionDetailsDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Submission data to display */
  submission: Submission | null | undefined;

  /** Whether submission data is loading */
  loading?: boolean;

  /** Form configuration to compare fields (optional - will use form from submission if not provided) */
  form?: Form | null;

  /** Form fields for labels (optional - will use formFields from form if not provided) */
  formFields?: FormField[] | undefined;

  /** Allow status changes */
  allowStatusChange?: boolean;

  /** Callback when status is changed */
  onStatusChanged?: (newStatus: number) => void;
}

export const SubmissionDetailsDrawer: React.FC<
  SubmissionDetailsDrawerProps
> = ({
  opened,
  onClose,
  submission,
  loading = false,
  form,
  formFields,
  allowStatusChange = true,
  onStatusChanged,
}) => {
  const { user } = useApp();
  const canEdit = form?.rights?.includes(OPERATION_RIGHTS.UPDATE);

  // Determine if logo should be shown (hide for Business plan)
  const currentPlan = user?.account?.currentPlan;
  const showLogo = currentPlan !== "business";

  // Use form from submission if not provided
  const formToUse = form || submission?.form || null;
  const formFieldsToUse = formFields || formToUse?.formFields || undefined;

  // PDF download hook
  const { isGenerating, downloadPdf, downloadPdfWithMetadata } = usePdfDownload(
    {
      submission: submission || null,
      form: formToUse,
      formFields: formFieldsToUse,
      showLogo,
    }
  );

  // Handle print action - opens print page in new tab
  const handlePrint = () => {
    if (!submission?.id) return;
    window.open(`/submissions/${submission.id}/print`, "_blank");
  };

  const removeRights =
    submission?.rights && submission?.rights?.length > 0
      ? submission?.rights?.includes(OPERATION_RIGHTS.UPDATE)
      : canEdit;

  // PDF dropdown button
  const pdfDropdownButton = (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <AppButton
          variant="primary"
          iconName="Download"
          disabled={loading || !submission || isGenerating}
          loading={isGenerating}
        >
          Download PDF
        </AppButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<AppIcon iconName="FileText" size={16} />}
          onClick={downloadPdf}
        >
          Form data only
        </Menu.Item>
        <Menu.Item
          leftSection={<AppIcon iconName="FileText" size={16} />}
          onClick={downloadPdfWithMetadata}
        >
          Include metadata
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title="Submission Details"
      position="right"
      size="lg"
      loading={false}
      actions={[
        {
          label: "Close",
          variant: "secondary",
          onClick: onClose,
        },
        {
          label: "Print",
          variant: "secondary",
          iconName: "Printer",
          onClick: handlePrint,
          disabled: loading || !submission,
        },
      ]}
      customActions={pdfDropdownButton}
    >
      {loading ? (
        <SubmissionDetailsSkeleton />
      ) : submission ? (
        <Stack gap="xl">
          <SubmissionMetadata
            submission={submission}
            allowStatusChange={removeRights && allowStatusChange}
            onStatusChanged={onStatusChanged}
          />

          <Divider />

          <SubmissionFormData
            submission={submission}
            form={formToUse}
            formFields={formFieldsToUse}
          />
        </Stack>
      ) : null}
    </AppDrawer>
  );
};
